// src/utils/returnTo.ts
//
// Where to go after signing in (#948). A link to a signed-in page, such as the #938
// download email's /downloads, sends a signed-out visitor to /login. Google and Apple
// sign-in leave the app for the provider and come back to the site's root, so the
// router's own state is gone by then. The destination waits in sessionStorage instead:
// per tab, so it survives that round trip and reaches nothing else.

const KEY = 'chaptery.returnTo';
const MAX_AGE_MS = 30 * 60 * 1000;
// Pages that are part of signing in or out: returning to one would go round in a loop,
// or sign the person straight back out.
const AUTH_PATHS = ['/login', '/register', '/recover', '/reset-password', '/setup-recovery-key', '/signout'];

/** A path on this site worth returning to, or null. Never another origin. */
export function safeReturnPath(path: unknown): string | null {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  // Browsers drop tabs and newlines from URLs, which can turn "/<tab>/x" into "//x".
  for (let i = 0; i < path.length; i += 1) {
    const code = path.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return null;
  }
  let url: URL;
  try {
    url = new URL(path, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  // Checked as the router will see it: dot segments resolved, any case, with or without
  // a trailing slash. So "/downloads/../login" is the sign-in page it really is.
  // The router decodes each segment before matching, so "/%6Cogin" is /login to it too.
  let decoded: string;
  try {
    decoded = decodeURIComponent(url.pathname);
  } catch {
    return null;
  }
  const pathname = decoded.toLowerCase().replace(/\/+$/, '') || '/';
  if (pathname === '/' || AUTH_PATHS.includes(pathname)) return null;
  return url.pathname + url.search + url.hash;
}

export function rememberReturnTo(path: string, now = Date.now()): void {
  const safe = safeReturnPath(path);
  if (!safe) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ path: safe, at: now }));
  } catch {
    // Storage unavailable: sign-in still works, and lands on the default page.
  }
}

/** The remembered destination while it is fresh, without forgetting it. */
export function peekReturnTo(now = Date.now()): string | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof at !== 'number' || at > now || now - at > MAX_AGE_MS) return null;
    return safeReturnPath(path);
  } catch {
    return null;
  }
}

/** The remembered destination while it is fresh, forgotten as it is read: used once. */
export function takeReturnTo(now = Date.now()): string | null {
  const path = peekReturnTo(now);
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: there was nothing to forget.
  }
  return path;
}
